/**
 * Dynamic B2E Setting Provider
 *
 * This provider dynamically selects settings based on the current brand
 * It replaces the need for separate ProtektaB2eSettingProvider
 */

import { Injectable, inject } from '@angular/core';

import { MobiUiLanguage } from '@mobi/rwc-ui-components-ng-jslib';
import { FaviconService } from '@mobi/rwc-uxframework-ng';

import { B2C, B2E } from '../common.constant';
import { AppSettingProvider, getLinks } from '../core/setting';

import { BrandConfigService } from './brand-config.service';

/**
 * Base class for dynamic setting providers
 * Provides common brand-specific setup logic
 */
abstract class DynamicSettingProviderBase implements AppSettingProvider {
    protected brandConfigService = inject(BrandConfigService);
    protected faviconService = inject(FaviconService, { optional: true });

    constructor() {
        this.initializeBrandSpecificSetup();
    }

    /**
     * Initialize brand-specific setup (e.g., favicon)
     */
    protected initializeBrandSpecificSetup() {
        const brand = this.brandConfigService.getBrand();

        if (!this.faviconService) {
            return;
        }

        if (brand.faviconSource === 'protekta') {
            this.faviconService.activateProtektaFavicon();
        } else {
            this.faviconService.activateMobiliarFavicon();
        }
    }

    /**
     * Get application settings based on current brand
     * Must be implemented by subclasses
     */
    abstract getAppSetting(): ReturnType<AppSettingProvider['getAppSetting']>;
}

@Injectable()
export class DynamicB2eSettingProvider extends DynamicSettingProviderBase {
    /**
     * Get application settings based on current brand
     */
    getAppSetting() {
        const brand = this.brandConfigService.getBrand();

        return {
            source: brand.appSource,
            context: B2E,
            language: 'de' as MobiUiLanguage,
            links: getLinks('de', brand.appSource),
        };
    }
}

/**
 * Dynamic B2C Setting Provider
 *
 * Similar to B2E provider but for B2C context
 */
@Injectable()
export class DynamicB2cSettingProvider extends DynamicSettingProviderBase {
    getAppSetting() {
        const brand = this.brandConfigService.getBrand();

        return {
            source: brand.appSource,
            context: B2C,
            language: 'de' as MobiUiLanguage,
            links: getLinks('de', brand.appSource),
        };
    }
}
